import React from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Loader2, RefreshCw, RotateCcw, Search, X } from "lucide-react";
import SidebarNavIcon from "../ui/SidebarNavIcon";

export const FilterRailHead = ({
  brandLabel,
  brandSub,
  kicker,
  title,
  activeCount,
  collapsed,
  compact,
  onToggleCollapse,
  onClose,
  brandIcon: BrandIcon,
}) => {
  const iconOnly = collapsed && !compact;
  const label = brandLabel || kicker;
  const sub = brandSub || title;

  return (
    <div className={`bp-filter-brand-row${iconOnly ? " bp-filter-brand-row-icon-only" : ""}`}>
      <div className="bp-filter-brand">
        {!iconOnly ? (
          <>
            <span className="bp-filter-brand-label">{label}</span>
            <span className="bp-filter-brand-sub">{sub}</span>
            {activeCount > 0 ? (
              <span className="bp-filter-brand-tag">
                <span className="bp-filter-brand-pip" aria-hidden />
                {activeCount} active
              </span>
            ) : null}
          </>
        ) : BrandIcon ? (
          <span className="bp-filter-brand-mark" aria-hidden>
            <BrandIcon size={14} strokeWidth={2.4} />
          </span>
        ) : (
          <span className="bp-filter-brand-mark" aria-hidden>
            ◈
          </span>
        )}
      </div>
      {!compact && onToggleCollapse ? (
        <button
          type="button"
          className="bp-filter-collapse"
          onClick={onToggleCollapse}
          aria-label={iconOnly ? "Expand filters" : "Collapse filters"}
          title={iconOnly ? "Expand" : "Collapse"}
        >
          {iconOnly ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      ) : null}
      {compact && onClose ? (
        <button type="button" className="bp-filter-collapse" onClick={onClose} aria-label="Close filters">
          <X size={14} />
        </button>
      ) : null}
    </div>
  );
};

export const FilterSearch = ({ value, onChange, placeholder = "Filter markets…", collapsed }) => {
  if (collapsed) return null;

  return (
    <div className="bp-filter-search">
      <Search size={13} className="bp-filter-search-ico" aria-hidden />
      <input
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bp-filter-search-input"
        aria-label="Search markets"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          className="bp-filter-search-clear"
          aria-label="Clear search"
        >
          <X size={11} />
        </button>
      ) : null}
    </div>
  );
};

export const FilterChip = ({ label, active, onClick, count, title }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`bp-filter-chip${active ? " bp-filter-chip-active" : ""}`}
    title={title}
  >
    <span>{label}</span>
    {count != null ? <span className="bp-filter-chip-count">{count}</span> : null}
  </button>
);

export const FilterChips = ({ children, collapsed, label = "Status" }) => {
  if (collapsed) return null;

  return (
    <div className="bp-filter-chips-wrap">
      <span className="bp-filter-chips-label">{label}</span>
      <div className="bp-filter-chips" role="tablist" aria-label={label}>
        {children}
      </div>
    </div>
  );
};

export const FilterToolbar = ({
  sortKey,
  sortOptions,
  onSortChange,
  onRefresh,
  refreshing,
  isLoading,
  collapsed,
}) => {
  if (collapsed) return null;

  return (
    <div className="bp-filter-toolbar">
      <label className="bp-filter-sort">
        <span className="bp-filter-sort-label">Sort</span>
        <span className="bp-filter-sort-wrap">
          <select
            value={sortKey}
            onChange={(e) => onSortChange(e.target.value)}
            className="bp-filter-sort-select"
            aria-label="Sort markets"
          >
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown size={11} className="bp-filter-sort-chev" aria-hidden />
        </span>
      </label>
      <button
        type="button"
        onClick={onRefresh}
        disabled={isLoading || refreshing}
        className="bp-filter-refresh"
        aria-label="Refresh markets"
        title="Refresh"
      >
        {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
      </button>
    </div>
  );
};

export const FilterSummary = ({ children, collapsed }) => {
  if (collapsed) return null;

  return (
    <div className="bp-filter-summary" aria-live="polite">
      {children}
    </div>
  );
};

export const FilterRailSummary = ({ stats, headline, urgent, collapsed }) => {
  if (collapsed) return null;

  return (
    <header className="bp-filter-nav-summary-wrap">
      <div className="bp-filter-nav-summary" aria-label="Desk summary">
        {stats.map(({ label, value, tone }) => (
          <div key={label} className="bp-filter-nav-summary-stat">
            <em>{label}</em>
            <strong className={`tabular-nums${tone ? ` bp-filter-nav-summary-${tone}` : ""}`}>
              {value}
            </strong>
          </div>
        ))}
      </div>
      {headline ? (
        <p className="bp-filter-nav-headline">
          {urgent ? <span className="bp-filter-nav-pulse" aria-hidden /> : null}
          {headline}
        </p>
      ) : null}
    </header>
  );
};

export const FilterGroup = ({ label, collapsed, children, role = "presentation" }) => (
  <div className="bp-filter-nav-group">
    {!collapsed ? <span className="bp-filter-nav-section-label">{label}</span> : null}
    <div className="bp-filter-nav-stack" role={role}>
      {children}
    </div>
  </div>
);

export const FilterShell = ({ children, collapsed }) => {
  if (collapsed) return <div className="bp-predict-nav-scroll">{children}</div>;

  return (
    <div className="bp-predict-nav-scroll">
      <div className="bp-filter-shell">{children}</div>
    </div>
  );
};

export const FilterShellSection = ({ label, icon: Icon, collapsed, children }) => {
  if (collapsed) {
    return (
      <div className="bp-filter-nav-group">
        <div className="bp-filter-nav-stack">{children}</div>
      </div>
    );
  }

  return (
    <div className="bp-filter-shell-section">
      <p className="bp-filter-shell-label">
        {Icon ? <Icon size={11} aria-hidden /> : null}
        {label}
      </p>
      <div className="bp-filter-discovery-grid">{children}</div>
    </div>
  );
};

export const FilterDiscoveryChip = ({
  active,
  onClick,
  icon,
  label,
  hint,
  count,
  logo,
  collapsed,
  urgent,
}) => {
  if (collapsed) {
    return (
      <FilterLane
        active={active}
        onClick={onClick}
        icon={icon}
        logo={logo}
        label={label}
        hint={hint}
        collapsed
        urgent={urgent}
      />
    );
  }

  return (
    <button
      type="button"
      className={`bp-filter-discovery-chip${active ? " bp-filter-discovery-chip-active" : ""}${
        urgent && !active ? " bp-filter-discovery-chip-urgent" : ""
      }`}
      onClick={onClick}
      title={hint ? `${label} — ${hint}` : label}
    >
      {logo ? (
        <span className="bp-filter-discovery-chip-icon bp-filter-discovery-chip-crest">
          <img src={logo} alt="" />
        </span>
      ) : (
        <span className="bp-filter-discovery-chip-icon">
          <SidebarNavIcon icon={icon} size={12} />
        </span>
      )}
      <span className="bp-filter-discovery-chip-copy">
        <span>{label}</span>
        {hint ? <small>{hint}</small> : null}
      </span>
      {count != null ? <strong className="tabular-nums">{count}</strong> : null}
    </button>
  );
};

export const FilterLane = ({
  active,
  onClick,
  icon,
  logo,
  label,
  hint,
  count,
  collapsed,
  urgent,
  role,
  ariaSelected,
}) => (
  <button
    type="button"
    role={role}
    aria-selected={ariaSelected}
    aria-pressed={role ? undefined : active}
    aria-label={label}
    title={collapsed && hint ? `${label} — ${hint}` : undefined}
    className={`bp-filter-nav-lane${active ? " bp-filter-nav-lane-on" : ""}${
      urgent && !active ? " bp-filter-nav-lane-urgent" : ""
    }`}
    onClick={onClick}
  >
    <span className="bp-filter-nav-lane-bar" aria-hidden />
    {logo ? (
      <span className="bp-filter-nav-lane-crest">
        <img src={logo} alt="" />
      </span>
    ) : (
      <span className="bp-filter-nav-lane-glyph" aria-hidden>
        <SidebarNavIcon icon={icon} size={13} />
      </span>
    )}
    {!collapsed ? (
      <>
        <span className="bp-filter-nav-lane-copy">
          <strong>{label}</strong>
          {hint ? <em>{hint}</em> : null}
        </span>
        {count != null ? (
          <span className="bp-filter-nav-lane-count tabular-nums">{count}</span>
        ) : null}
      </>
    ) : null}
  </button>
);

export const FilterRailClear = ({ activeCount, collapsed, onClear }) => {
  if (!activeCount) return null;

  return (
    <button
      type="button"
      className={`bp-filter-nav-clear${collapsed ? " bp-filter-nav-clear-icon" : ""}`}
      onClick={onClear}
      aria-label={`Clear ${activeCount} filter${activeCount === 1 ? "" : "s"}`}
      title={`Clear ${activeCount} filter${activeCount === 1 ? "" : "s"}`}
    >
      {collapsed ? (
        <RotateCcw size={14} strokeWidth={2.4} />
      ) : (
        <>
          <RotateCcw size={11} aria-hidden />
          Clear {activeCount} filter{activeCount === 1 ? "" : "s"}
        </>
      )}
    </button>
  );
};
